import { Box, Switch, TextField, Typography, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import TimerIcon from "@mui/icons-material/Timer";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const AuthenticatorTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, settings = {}, setSettings, accentColor = "#06b6d4" }) => {
  const length = Number(settings.length) || 6;
  const method = settings.method || "email";
  const expiry = Number(settings.expiry) || 5;
  const resendCooldown = Number(settings.resendCooldown) || 60;
  const codeType = settings.codeType || "numeric";
  const sendTo = settings.sendTo || "visitor_email";

  const update = (patch) => setSettings({ ...settings, ...patch });

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip verification</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <VpnKeyIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Code Configuration</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Code length</InputLabel>
            <Select value={length} label="Code length"
              onChange={(e) => update({ length: e.target.value })}
              sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
            >
              {[4, 5, 6, 8].map((l) => (
                <MenuItem key={l} value={l} sx={{ fontSize: 12 }}>{l} digits</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Code type</InputLabel>
            <Select value={codeType} label="Code type"
              onChange={(e) => update({ codeType: e.target.value })}
              sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
            >
              <MenuItem value="numeric" sx={{ fontSize: 12 }}>Numbers only (123456)</MenuItem>
              <MenuItem value="alphanumeric" sx={{ fontSize: 12 }}>Alphanumeric (A1B2C3)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <EmailIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Delivery Method</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.8, mb: 1.5 }}>
          {[{ v: "email", l: "Email", i: <EmailIcon sx={{ fontSize: 14 }} /> }, { v: "sms", l: "SMS", i: <SmsIcon sx={{ fontSize: 14 }} /> }].map((m) => (
            <Chip key={m.v} icon={m.i} label={m.l} size="small" onClick={() => update({ method: m.v })}
              sx={{ fontWeight: 700, fontSize: 11, bgcolor: method === m.v ? `${accentColor}15` : "#f3f4f6", color: method === m.v ? accentColor : "#6b7280", border: `1px solid ${method === m.v ? accentColor : "transparent"}`, "&:hover": { bgcolor: method === m.v ? `${accentColor}22` : "#e5e7eb" }, "& .MuiChip-icon": { color: method === m.v ? accentColor : "#6b7280" } }}
            />
          ))}
        </Box>
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ "&.Mui-focused": { color: accentColor } }}>Send to</InputLabel>
          <Select value={sendTo} label="Send to"
            onChange={(e) => update({ sendTo: e.target.value })}
            sx={{ borderRadius: 2, bgcolor: "#fff", fontSize: 12, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: accentColor } }}
          >
            <MenuItem value="visitor_email" sx={{ fontSize: 12 }}>Visitor's email (from pre-chat)</MenuItem>
            <MenuItem value="ask_email" sx={{ fontSize: 12 }}>Ask for email first</MenuItem>
            <MenuItem value="ask_phone" sx={{ fontSize: 12 }}>Ask for phone first</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 10, color: "#6b7280", mt: 0.8 }}>For "ask_email" or "ask_phone", a previous question must have collected the contact.</Typography>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <TimerIcon sx={{ fontSize: 16, color: accentColor }} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Timing</Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField fullWidth size="small" type="number" label="Code expiry (min)"
            value={expiry} onChange={(e) => update({ expiry: Number(e.target.value) || 5 })}
            sx={accentText(accentColor)} />
          <TextField fullWidth size="small" type="number" label="Resend cooldown (s)"
            value={resendCooldown} onChange={(e) => update({ resendCooldown: Number(e.target.value) || 60 })}
            sx={accentText(accentColor)} />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Invalid code. Please try again." value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default AuthenticatorTab;
